import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import Loading from "@/components/app/Loading";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { RefreshCw, Ban, Copy, Search } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("codes");
  const [codes, setCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [newCodeEmail, setNewCodeEmail] = useState("");
  const [newCodeProduct, setNewCodeProduct] = useState("quiz");
  const [generating, setGenerating] = useState(false);

  // Order-lookup diagnostic (owner-only). Uses session auth via adminApi.
  const [lookupQ, setLookupQ] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const runLookup = async () => {
    const q = lookupQ.trim();
    if (!q) return;
    setLookupBusy(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const res = await adminApi.orderLookup(q);
      setLookupResult(res);
    } catch (e) {
      setLookupError(e.message || "Lookup failed");
    } finally {
      setLookupBusy(false);
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [c, cust, prod] = await Promise.all([
        adminApi.listCodes(),
        adminApi.listCustomers(q),
        adminApi.listProducts().catch(() => ({ products: [] })),
      ]);
      setCodes(c || []);
      setCustomers(cust.customers || []);
      setProducts(prod.products || []);
    } catch (e) {
      toast.error(e.message || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCode = async () => {
    setGenerating(true);
    try {
      const res = await adminApi.generateCode(newCodeEmail || null, newCodeProduct);
      toast.success(`Generated ${res.code}`);
      setNewCodeEmail("");
      reload();
    } catch (e) {
      toast.error(e.message || "Could not generate code");
    } finally {
      setGenerating(false);
    }
  };

  const voidCode = async (code) => {
    if (!window.confirm(`Void code ${code}?`)) return;
    try { await adminApi.voidCode(code); reload(); toast.success("Voided"); } catch (e) { toast.error(e.message); }
  };
  const resetCode = async (code) => {
    if (!window.confirm(`Reset code ${code} to unused?`)) return;
    try { await adminApi.resetCode(code); reload(); toast.success("Reset"); } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6" data-testid={IDS.adminPage}>
      <header>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Admin</h1>
        <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>Owner tools: codes, customers, products.</p>
      </header>

      <div className="flex gap-2 flex-wrap">
        {["codes", "customers", "products", "lookup"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="px-3 py-2 text-sm rounded-md"
            data-testid={`admin-tab-${t}`}
            style={{
              background: tab === t ? "rgba(201,162,39,0.12)" : "transparent",
              color: tab === t ? "var(--pa-gold)" : "var(--pa-text-secondary)",
              border: `1px solid ${tab === t ? "var(--pa-gold-dark)" : "var(--pa-card-border)"}`,
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "codes" && (
        <div className="space-y-4">
          <section className="pa-card p-5 space-y-3">
            <h2 className="pa-serif text-lg" style={{ color: "var(--pa-gold)" }}>Generate access code</h2>
            <input
              className="pa-input"
              placeholder="Purchaser email (optional)"
              value={newCodeEmail}
              onChange={(e) => setNewCodeEmail(e.target.value)}
              data-testid="admin-new-code-email"
            />
            <select
              className="pa-input"
              value={newCodeProduct}
              onChange={(e) => setNewCodeProduct(e.target.value)}
              data-testid="admin-new-code-product"
            >
              {["quiz", "starter_kit", "guidebook", "manual", "bundle_complete"].map((pid) => (
                <option key={pid} value={pid}>{pid}</option>
              ))}
            </select>
            <button
              onClick={generateCode}
              disabled={generating}
              className="pa-btn-gold"
              data-testid={IDS.adminGenerateBtn}
            >
              {generating ? "Generating..." : "Generate code"}
            </button>
          </section>

          <section className="pa-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="pa-serif text-lg" style={{ color: "var(--pa-gold)" }}>All codes ({codes.length})</h2>
              <button className="pa-btn-ghost text-xs inline-flex items-center gap-1" onClick={reload}>
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ color: "var(--pa-text-muted)" }}>
                    <th className="text-left py-1 pr-3">Code</th>
                    <th className="text-left py-1 pr-3">Product</th>
                    <th className="text-left py-1 pr-3">Email</th>
                    <th className="text-left py-1 pr-3">Status</th>
                    <th className="text-left py-1 pr-3" data-testid="admin-codes-delivery-header">Delivery</th>
                    <th className="text-left py-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...codes]
                    .sort((a, b) => {
                      // Failed deliveries first (so owner catches them at a glance),
                      // then most-recently-created.
                      const aFail = a.email_sent === false ? 1 : 0;
                      const bFail = b.email_sent === false ? 1 : 0;
                      if (aFail !== bFail) return bFail - aFail;
                      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
                      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
                      return bt - at;
                    })
                    .slice(0, 100)
                    .map((c) => {
                    const status = c.voided ? "voided" : c.used ? "used" : "unused";
                    return (
                      <tr key={c.code} style={{ borderTop: "1px solid var(--pa-card-border)", color: "var(--pa-text-primary)" }}>
                        <td className="py-2 pr-3 font-mono">
                          {c.code}
                          <button className="ml-1 pa-btn-ghost" onClick={() => navigator.clipboard.writeText(c.code)} title="Copy">
                            <Copy size={11} />
                          </button>
                        </td>
                        <td className="py-2 pr-3">{c.product_id || "quiz"}</td>
                        <td className="py-2 pr-3">{c.purchaser_email || c.redeemed_by_email || "-"}</td>
                        <td className="py-2 pr-3" style={{ color: status === "unused" ? "var(--pa-success)" : status === "voided" ? "var(--pa-error)" : "var(--pa-text-secondary)" }}>{status}</td>
                        <td
                          className="py-2 pr-3"
                          data-testid={`admin-code-delivery-${c.code}`}
                          style={{
                            color: c.email_sent === true ? "var(--pa-success)"
                              : c.email_sent === false ? "var(--pa-error)"
                              : "var(--pa-text-muted)",
                            maxWidth: "260px",
                          }}
                        >
                          {c.email_sent === true && "Sent"}
                          {c.email_sent === false && (
                            <span title={c.email_error || "no reason recorded"}>
                              Failed
                              {c.email_error ? (
                                <span
                                  data-testid={`admin-code-email-error-${c.code}`}
                                  className="block text-[10px] mt-0.5 truncate"
                                  style={{ color: "var(--pa-text-muted)" }}
                                >
                                  {c.email_error}
                                </span>
                              ) : null}
                            </span>
                          )}
                          {(c.email_sent === null || c.email_sent === undefined) && "—"}
                        </td>
                        <td className="py-2 flex gap-2">
                          {!c.voided && (
                            <button onClick={() => voidCode(c.code)} className="pa-btn-ghost text-xs inline-flex items-center gap-1" style={{ color: "var(--pa-error)" }}>
                              <Ban size={11} /> Void
                            </button>
                          )}
                          {(c.used || c.voided) && (
                            <button onClick={() => resetCode(c.code)} className="pa-btn-ghost text-xs">Reset</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {tab === "customers" && (
        <section className="pa-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <input
              className="pa-input"
              placeholder="Search email or name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && reload()}
              data-testid="admin-customer-search"
            />
            <button className="pa-btn-outline text-sm" onClick={reload}>Search</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ color: "var(--pa-text-muted)" }}>
                  <th className="text-left py-1 pr-3">Name</th>
                  <th className="text-left py-1 pr-3">Email</th>
                  <th className="text-left py-1 pr-3">Verified</th>
                  <th className="text-left py-1 pr-3">Products</th>
                  <th className="text-left py-1 pr-3" data-testid="admin-customers-primary-header">Primary archetype</th>
                  <th className="text-left py-1 pr-3" data-testid="admin-customers-secondary-header">Secondary archetype</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => {
                  const primary = (c.primary_archetypes || []).filter(Boolean);
                  const secondary = (c.secondary_archetypes || []).filter(Boolean);
                  return (
                    <tr key={c.user_id} style={{ borderTop: "1px solid var(--pa-card-border)", color: "var(--pa-text-primary)" }}>
                      <td className="py-2 pr-3">{c.name}</td>
                      <td className="py-2 pr-3">{c.email}</td>
                      <td className="py-2 pr-3">{c.is_verified ? "✓" : "-"}</td>
                      <td className="py-2 pr-3">{(c.products || []).join(", ")}</td>
                      <td
                        className="py-2 pr-3"
                        data-testid={`admin-customer-primary-${c.user_id}`}
                        style={{ color: primary.length ? "var(--pa-gold)" : "var(--pa-text-muted)" }}
                      >
                        {primary.length ? primary.join(", ") : "—"}
                      </td>
                      <td
                        className="py-2 pr-3"
                        data-testid={`admin-customer-secondary-${c.user_id}`}
                        style={{ color: secondary.length ? "var(--pa-text-secondary)" : "var(--pa-text-muted)" }}
                      >
                        {secondary.length ? secondary.join(", ") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "lookup" && (
        <div className="space-y-4">
          <section className="pa-card p-5 space-y-3">
            <h2 className="pa-serif text-lg" style={{ color: "var(--pa-gold)" }}>Order &amp; access-code diagnostic</h2>
            <p className="text-xs" style={{ color: "var(--pa-text-secondary)" }}>
              Look up any customer by <strong>email</strong> or Wix <strong>order number/id</strong> (partial match).
              Owner-authenticated — the Wix webhook secret is never exposed to the browser.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input
                className="pa-input flex-1"
                placeholder="customer@example.com  or  Wix order id"
                value={lookupQ}
                onChange={(e) => setLookupQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runLookup()}
                data-testid={IDS.adminLookupInput}
                style={{ minWidth: 260 }}
              />
              <button
                onClick={runLookup}
                disabled={lookupBusy || !lookupQ.trim()}
                className="pa-btn-gold inline-flex items-center gap-2"
                data-testid={IDS.adminLookupBtn}
              >
                <Search size={14} />
                {lookupBusy ? "Looking up…" : "Look up"}
              </button>
            </div>
            {lookupError && (
              <p className="text-sm" style={{ color: "var(--pa-error)" }}>{lookupError}</p>
            )}
          </section>

          {lookupResult && (
            <section className="pa-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="pa-serif text-base" style={{ color: "var(--pa-text-primary)" }}>
                  Results for <span style={{ color: "var(--pa-gold)" }}>{lookupResult.query}</span>
                </h3>
                <span className="text-xs" style={{ color: "var(--pa-text-muted)" }}>
                  {lookupResult.count} row{lookupResult.count === 1 ? "" : "s"}
                </span>
              </div>

              <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
                <div className="pa-card p-3">
                  <p style={{ color: "var(--pa-text-muted)" }}>Customer has signed in</p>
                  <p className="pa-serif text-lg mt-1" style={{
                    color: lookupResult.signed_in === true ? "var(--pa-success)"
                      : lookupResult.signed_in === false ? "var(--pa-error)"
                      : "var(--pa-text-secondary)"
                  }}>
                    {lookupResult.signed_in === true ? "Yes" : lookupResult.signed_in === false ? "No" : "N/A (order-id query)"}
                  </p>
                </div>
                <div className="pa-card p-3">
                  <p style={{ color: "var(--pa-text-muted)" }}>Account email on record</p>
                  <p className="pa-serif text-base mt-1 break-all" style={{ color: "var(--pa-text-primary)" }}>
                    {lookupResult.account_email || "—"}
                  </p>
                </div>
              </div>

              {(lookupResult.primary_archetypes?.length > 0 || lookupResult.secondary_archetypes?.length > 0) && (
                <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
                  <div className="pa-card p-3">
                    <p style={{ color: "var(--pa-text-muted)" }}>Primary archetype{lookupResult.primary_archetypes?.length > 1 ? "s" : ""}</p>
                    <p className="pa-serif text-base mt-1" style={{ color: "var(--pa-gold)" }}>
                      {lookupResult.primary_archetypes?.join(", ") || "—"}
                    </p>
                  </div>
                  <div className="pa-card p-3">
                    <p style={{ color: "var(--pa-text-muted)" }}>Secondary archetype{lookupResult.secondary_archetypes?.length > 1 ? "s" : ""}</p>
                    <p className="pa-serif text-base mt-1" style={{ color: "var(--pa-text-primary)" }}>
                      {lookupResult.secondary_archetypes?.length ? lookupResult.secondary_archetypes.join(", ") : "— (customer has not taken the quiz)"}
                    </p>
                  </div>
                </div>
              )}

              {/* Owner recovery action — one click generates a fresh code +
                  fires the standard onboarding email. No DevTools needed. */}
              {lookupQ.includes("@") && (
                <div className="pa-card p-3 mb-4" style={{ borderColor: "var(--pa-gold-dark)" }}>
                  <p className="text-xs mb-2" style={{ color: "var(--pa-text-secondary)" }}>
                    <strong>Recovery action</strong> — generate a fresh code for this email AND automatically send the standard onboarding email (app link, sign-in instructions, code). Safe to run multiple times; each call mints a new unused code.
                  </p>
                  <button
                    className="pa-btn-gold inline-flex items-center gap-2"
                    disabled={lookupBusy}
                    data-testid="admin-fulfill-resend-btn"
                    onClick={async () => {
                      if (!window.confirm(`Generate a new access code for ${lookupResult.query} and email it to them?`)) return;
                      setLookupBusy(true);
                      try {
                        const res = await adminApi.generateCode(lookupResult.query, "quiz");
                        if (res.success) {
                          if (res.email_sent) {
                            toast.success(`Code ${res.code} created — onboarding email sent`);
                          } else {
                            toast.error(`Code ${res.code} created BUT email failed: ${res.email_error || "unknown reason"}`, { duration: 15000 });
                          }
                          runLookup();
                        } else {
                          toast.error("Failed to generate code");
                        }
                      } catch (e) {
                        toast.error(e.message || "Recovery failed");
                      } finally {
                        setLookupBusy(false);
                      }
                    }}
                  >
                    Generate + Email Access Code
                  </button>
                </div>
              )}

              {/* Move all unused codes for this email to a different (Google-backed)
                  address. Use this when a customer bought under a non-Gmail email
                  (Yahoo, iCloud, Outlook, AOL) that cannot sign in via Google Auth.
                  After moving, the customer signs in with the new email and their
                  entitlement is auto-linked — no code typing needed. */}
              {lookupQ.includes("@") && (() => {
                const movable = (lookupResult.results || []).filter(
                  (r) => r.code && !r.used && !r.voided,
                );
                if (movable.length === 0) return null;
                return (
                  <div className="pa-card p-3 mb-4" style={{ borderColor: "var(--pa-gold-dark)" }}>
                    <p className="text-xs mb-2" style={{ color: "var(--pa-text-secondary)" }}>
                      <strong>Change purchaser email</strong> — customer bought under a non-Gmail address they can't sign in with? Move their <strong>{movable.length} unused code{movable.length === 1 ? "" : "s"}</strong> to a Google-backed email (Gmail / Workspace). They then sign in with the new email and everything auto-links.
                    </p>
                    <button
                      className="pa-btn-gold inline-flex items-center gap-2"
                      disabled={lookupBusy}
                      data-testid="admin-change-purchaser-email-btn"
                      onClick={async () => {
                        const next = window.prompt(
                          `Move ${movable.length} unused code${movable.length === 1 ? "" : "s"} from ${lookupResult.query} to a new email.\n\nEnter the new (Google-backed) email:`,
                          "",
                        );
                        if (!next) return;
                        const trimmed = next.trim();
                        if (!trimmed || trimmed.toLowerCase() === (lookupResult.query || "").toLowerCase()) return;
                        setLookupBusy(true);
                        try {
                          let ok = 0;
                          const failures = [];
                          for (const r of movable) {
                            try {
                              await adminApi.setPurchaserEmail(r.code, trimmed);
                              ok += 1;
                            } catch (e) {
                              failures.push(`${r.code}: ${e.message || "failed"}`);
                            }
                          }
                          if (ok > 0 && failures.length === 0) {
                            toast.success(`Moved ${ok} code${ok === 1 ? "" : "s"} to ${trimmed}`);
                          } else if (ok > 0) {
                            toast.error(`Moved ${ok}, ${failures.length} failed:\n${failures.join("\n")}`, { duration: 15000 });
                          } else {
                            toast.error(`No codes moved:\n${failures.join("\n")}`, { duration: 15000 });
                          }
                          runLookup();
                        } finally {
                          setLookupBusy(false);
                        }
                      }}
                    >
                      Change email on {movable.length} unused code{movable.length === 1 ? "" : "s"}
                    </button>
                  </div>
                );
              })()}

              {lookupResult.count === 0 ? (
                <p className="text-sm" style={{ color: "var(--pa-text-muted)" }}>
                  No access codes or Wix orders match this query.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ color: "var(--pa-text-muted)" }}>
                        <th className="text-left py-1 pr-3">Code</th>
                        <th className="text-left py-1 pr-3">Product</th>
                        <th className="text-left py-1 pr-3">Wix order id</th>
                        <th className="text-left py-1 pr-3">Source</th>
                        <th className="text-left py-1 pr-3">Purchaser</th>
                        <th className="text-left py-1 pr-3">Email sent</th>
                        <th className="text-left py-1 pr-3">Code used</th>
                        <th className="text-left py-1 pr-3">Redeemed by</th>
                        <th className="text-left py-1 pr-3">Entitlement active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupResult.results.map((r, i) => (
                        <tr key={r.code || i} style={{ borderTop: "1px solid var(--pa-card-border)", color: "var(--pa-text-primary)" }}>
                          <td className="py-2 pr-3 font-mono">
                            {r.code}
                            {r.code && (
                              <button
                                className="ml-1 pa-btn-ghost"
                                onClick={() => navigator.clipboard.writeText(r.code)}
                                title="Copy code"
                              >
                                <Copy size={11} />
                              </button>
                            )}
                            {r.voided && (
                              <span className="ml-2 text-xs" style={{ color: "var(--pa-error)" }}>voided</span>
                            )}
                          </td>
                          <td className="py-2 pr-3">{r.product_id || (r.needs_mapping ? "needs mapping" : "—")}</td>
                          <td className="py-2 pr-3 font-mono text-[10px] break-all" style={{ maxWidth: 220 }}>{r.order_id || "—"}</td>
                          <td className="py-2 pr-3">{r.source}</td>
                          <td className="py-2 pr-3 break-all">{r.purchaser_email || "—"}</td>
                          <td className="py-2 pr-3" style={{
                            color: r.email_sent === true ? "var(--pa-success)"
                              : r.email_sent === false ? "var(--pa-error)"
                              : "var(--pa-text-muted)"
                          }}>
                            {r.email_sent === true ? "Yes" : r.email_sent === false ? "No" : "—"}
                          </td>
                          <td className="py-2 pr-3" style={{
                            color: r.used ? "var(--pa-text-secondary)" : "var(--pa-success)"
                          }}>
                            {r.used ? `Used${r.used_via ? ` (${r.used_via})` : ""}` : "Unused"}
                          </td>
                          <td className="py-2 pr-3 break-all">{r.redeemed_by_email || "—"}</td>
                          <td className="py-2 pr-3" style={{
                            color: r.entitlement_active ? "var(--pa-success)" : "var(--pa-text-muted)"
                          }}>
                            {r.entitlement_active ? "Active" : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {tab === "products" && (
        <section className="pa-card p-5">
          <h2 className="pa-serif text-lg mb-3" style={{ color: "var(--pa-gold)" }}>Products ({products.length})</h2>
          <div className="space-y-2 text-sm">
            {products.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between py-2" style={{ borderTop: "1px solid var(--pa-card-border)" }}>
                <div>
                  <p style={{ color: "var(--pa-text-primary)" }}>{p.name}</p>
                  <p className="text-xs" style={{ color: "var(--pa-text-muted)" }}>{p.product_id} · ${(p.price_cents / 100).toFixed(2)}</p>
                </div>
                <span className="text-xs" style={{ color: p.active ? "var(--pa-success)" : "var(--pa-text-muted)" }}>
                  {p.active ? "active" : "inactive"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
